import { DirectLine, Activity, ConnectionStatus } from "botframework-directlinejs";
import type { AccountInfo } from "@azure/msal-browser";
import axios from "axios";
import { Subscription } from "rxjs/Subscription";
import {
  ensureDirectLineHealthy,
  stopHealthProbe,
} from "./directLineHealth";

let directLine: DirectLine | null = null;
let conversationMetadata: ConversationMetadata | null = null;
let connectionReadyPromise: Promise<DirectLine> | null = null;

// Phase 1: Store user passport and identity for attaching to activities
let userPassport: string | null = null;
let userEntraOid: string | null = null;
let passportExpiresAt: number | null = null; // Unix timestamp (seconds)

// Buffer before expiry to trigger proactive refresh (5 minutes)
const PASSPORT_REFRESH_BUFFER_SECONDS = 300;

// Type definitions
interface ConversationMetadata {
  conversationId: string;
  channelId: string;
  serviceUrl: string;
  user: null;
}

interface DirectLineTokenResponse {
  token: string;
  userPassport?: string;
}

/**
 * Parse the expiry time from a passport JWT (header.payload.signature format).
 * Returns the `exp` claim as a Unix timestamp (seconds), or null if unparseable.
 */
function parsePassportExpiry(passport: string): number | null {
  try {
    const parts = passport.split('.');
    if (parts.length !== 3) return null;
    // Base64url → base64: replace URL-safe chars and restore required padding
    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const pad = b64.length % 4;
    if (pad) b64 += '='.repeat(4 - pad);
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/**
 * Store a passport and extract its expiry time.
 */
function setPassport(passport: string): void {
  userPassport = passport;
  passportExpiresAt = parsePassportExpiry(passport);
}

interface ChannelData {
  userPassport?: string;
  delegatedToken?: string;
  message_type?: string;
  control_event?: string;
  [key: string]: any;
}

// Activity with typed channel data
type ActivityWithChannelData = Activity & {
  channelData?: ChannelData;
}

const CONNECTION_STATUS: Record<number, string> = {
  0: "Uninitialized",
  1: "Connecting",
  2: "Online",
  3: "ExpiredToken",
  4: "FailedToConnect",
  5: "Ended",
};

/**
 * Initialize DirectLine with user identity (Phase 1: SharePoint Permissions).
 *
 * Phase 2: If msalAccount is provided without userAccessToken, will acquire token
 * automatically for APIM authentication.
 *
 * @param {string|null} userAccessToken - Optional MSAL access token
 * @param {Object} msalAccount - MSAL account object with user info
 * @returns {Promise<DirectLine>} DirectLine instance
 */
export async function getDirectLine(
  userAccessToken: string | null = null,
  msalAccount: AccountInfo | null = null
): Promise<DirectLine> {
  // If directLine exists but is from a previous page load, reset it
  // This ensures clean state on page refresh
  if (directLine) {
    return directLine;
  }

  // Phase 2: If we have msalAccount but no token, acquire one for APIM
  // Use backend API token now that admin consent has been granted
  if (msalAccount && !userAccessToken) {
    try {
      const { acquireBackendApiToken } = await import("./utils/msalTokenService");
      userAccessToken = await acquireBackendApiToken();
    } catch (tokenError) {
      console.error("❌ Failed to acquire token for DirectLine:", tokenError);
      const { handleAuthError } = await import("./utils/authErrorHandler");
      handleAuthError();
      throw new Error('Authentication failed — redirecting to login');
    }
  }

  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };

  if (userAccessToken) {
    headers["Authorization"] = `Bearer ${userAccessToken}`;
  } else {
    console.warn("⚠️ No bearer token - APIM will reject the DirectLine token request");
  }

  // Store OID and username locally, and send in request body for passport signing
  let requestBody: { entraOid?: string; username?: string } = {};
  if (msalAccount) {
    userEntraOid = msalAccount.localAccountId || msalAccount.homeAccountId;
    const username = msalAccount.name || msalAccount.username;
    requestBody = {
      entraOid: userEntraOid,
      username,
    };
  } else {
    console.warn("⚠️ No MSAL account provided - DirectLine token will not include passport");
  }

  const res = await axios.post<DirectLineTokenResponse>(
    import.meta.env.VITE_DIRECTLINE_TOKEN_URL,
    requestBody,
    { headers }
  );

  const { token, userPassport: passport } = res.data;

  // Phase 1: Store passport for attaching to activities
  if (passport) {
    setPassport(passport);
  } else {
    console.warn("⚠️ No user passport in DirectLine response - identity validation will fail");
  }

  directLine = new DirectLine({
    token,
    domain: "https://europe.directline.botframework.com/v3/directline",
  });

  return directLine;
}

/**
 * Get user passport for attaching to activities.
 * Phase 1: This passport must be attached to every activity's channelData.
 *
 * @returns {string|null} User passport or null if not available
 */
export function getUserPassport(): string | null {
  return userPassport;
}

/**
 * Get user Entra OID.
 * Phase 1: For logging and debugging purposes.
 *
 * @returns {string|null} User's Entra Object ID or null
 */
export function getUserEntraOid(): string | null {
  return userEntraOid;
}

/**
 * Check if the passport is expired or near expiry.
 */
export function isPassportExpired(): boolean {
  if (!userPassport || !passportExpiresAt) return true;
  const nowSeconds = Math.floor(Date.now() / 1000);
  return nowSeconds >= (passportExpiresAt - PASSPORT_REFRESH_BUFFER_SECONDS);
}

/**
 * Refresh the user passport by calling the DirectLine token service.
 * This only updates the passport — it does NOT create a new DirectLine connection.
 * Requires a valid MSAL token (handled by acquireBackendApiToken).
 */
export async function refreshPassport(): Promise<void> {
  if (!userEntraOid) {
    throw new Error('Cannot refresh passport — no user OID stored');
  }

  const { acquireBackendApiToken } = await import("./utils/msalTokenService");
  let apiToken: string;
  try {
    apiToken = await acquireBackendApiToken();
  } catch (tokenError) {
    const { handleAuthError } = await import("./utils/authErrorHandler");
    handleAuthError();
    throw new Error('Passport refresh failed — token acquisition error');
  }

  const { getCurrentMsalAccount } = await import("./utils/msalTokenService");
  const account = getCurrentMsalAccount();
  const username = account?.name || account?.username;

  try {
    const res = await axios.post<DirectLineTokenResponse>(
      import.meta.env.VITE_DIRECTLINE_TOKEN_URL,
      { entraOid: userEntraOid, username },
      { headers: { "Content-Type": "application/json", "Authorization": `Bearer ${apiToken}` } }
    );

    if (res.data.userPassport) {
      setPassport(res.data.userPassport);
    } else {
      throw new Error('Token service did not return a passport');
    }
  } catch (reqError) {
    if (axios.isAxiosError(reqError) && reqError.response &&
        (reqError.response.status === 401 || reqError.response.status === 403)) {
      const { handleAuthError } = await import("./utils/authErrorHandler");
      handleAuthError();
      throw new Error('Passport refresh failed — auth error');
    }
    throw reqError;
  }
}

/**
 * Ensure the passport is valid before sending a message.
 * Refreshes if expired or near expiry.
 */
export async function ensureValidPassport(): Promise<void> {
  if (!isPassportExpired()) return;
  await refreshPassport();
  if (isPassportExpired()) {
    throw new Error('Passport is still expired after refresh attempt');
  }
}

export async function ensureDirectLineOnline(
  timeoutMs: number = 15000,
  userAccessToken: string | null = null,
  msalAccount: AccountInfo | null = null
): Promise<DirectLine> {
  const instance = await getDirectLine(userAccessToken, msalAccount);

  if (instance.connectionStatus$ && instance.connectionStatus$.value === ConnectionStatus.Online) {
    return instance;
  }

  if (connectionReadyPromise) {
    return connectionReadyPromise;
  }

  connectionReadyPromise = new Promise((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    let statusSubscription: Subscription | undefined;
    let primingSubscription: Subscription | undefined;

    const cleanup = () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = undefined;
      }
      if (statusSubscription) {
        statusSubscription.unsubscribe();
        statusSubscription = undefined;
      }
      if (primingSubscription) {
        primingSubscription.unsubscribe();
        primingSubscription = undefined;
      }
      connectionReadyPromise = null;
    };

    // Subscribe once so DirectLine actually opens the websocket
    primingSubscription = instance.activity$.subscribe({
      next: () => {},
      error: () => {}
    });

    // Resolve immediately if status already online (race with priming)
    if (instance.connectionStatus$ && instance.connectionStatus$.value === ConnectionStatus.Online) {
      cleanup();
      resolve(instance);
      return;
    }

    timeoutId = setTimeout(() => {
      cleanup();
      reject(
        new Error(
          `DirectLine did not reach Online state within ${
            timeoutMs / 1000
          } seconds`
        )
      );
    }, timeoutMs);

    statusSubscription = instance.connectionStatus$.subscribe({
      next: (status: ConnectionStatus) => {
        if (status === ConnectionStatus.Online) {
          cleanup();
          resolve(instance);
        } else if (status === ConnectionStatus.FailedToConnect || status === ConnectionStatus.Ended) {
          cleanup();
          reject(
            new Error(
              `DirectLine connection ended with status ${status} (${
                CONNECTION_STATUS[status] ?? "Unknown"
              })`
            )
          );
        }
      },
      error: (err: Error) => {
        cleanup();
        reject(err);
      },
    });
  });

  return connectionReadyPromise;
}

/**
 * @deprecated This function is no longer the primary flow for establishing DirectLine readiness.
 * The new pattern establishes a durable subscription BEFORE running the health probe.
 * See ChatSlice_Store.jsx initializeChatListener for the current implementation.
 *
 * Ensure DirectLine is online and healthy (ready to receive messages).
 * This combines the connection status check with a health probe roundtrip
 * to verify the backend can actually process messages on the websocket.
 *
 * NOTE: This approach has a race condition where control messages (like bootstrap_ack)
 * can arrive during the health probe before the durable subscription is set up.
 * Use the new pattern in ChatSlice_Store.jsx instead.
 *
 * @param {number} connectionTimeoutMs - Timeout for connection status
 * @param {number} healthProbeIntervalMs - Interval between health probes
 * @param {number} healthProbeTimeoutMs - Timeout for health probe acknowledgement
 * @returns {Promise<DirectLine>} DirectLine instance once healthy
 */
export async function ensureDirectLineHealthyAndReady(
  connectionTimeoutMs: number = 15000,
  healthProbeIntervalMs: number = 1000,
  healthProbeTimeoutMs: number = 30000
): Promise<DirectLine> {

  // Step 1: Wait for connection status to reach Online
  const instance = await ensureDirectLineOnline(connectionTimeoutMs);

  // Step 2: Run health probe to verify backend readiness
  await ensureDirectLineHealthy(
    instance,
    healthProbeIntervalMs,
    healthProbeTimeoutMs
  );

  return instance;
}

/**
 * Wait for and retrieve conversation metadata from DirectLine
 *
 * Phase 2: Now requires MSAL account to ensure DirectLine is initialized with bearer token
 * for APIM authentication.
 *
 * @param {Object} msalAccount - MSAL account object (required for APIM)
 * @returns {Promise<Object>} { conversationId, channelId, serviceUrl, user }
 */
export async function getConversationMetadata(msalAccount: AccountInfo | null = null): Promise<ConversationMetadata> {
  if (conversationMetadata) return conversationMetadata;

  // Phase 2: Pass MSAL account to ensure DirectLine token request includes bearer token for APIM
  const directLine = await ensureDirectLineOnline(15000, null, msalAccount);
  // Access private conversationId property via type assertion
  const directLineAny = directLine as any;

  // Wait for conversationId to be available
  return new Promise<ConversationMetadata>((resolve, reject) => {
    const timeout = setTimeout(() => {
      reject(new Error("Timeout waiting for DirectLine conversation"));
    }, 10000); // 10 second timeout

    // Check if already available
    if (directLineAny.conversationId) {
      clearTimeout(timeout);
      conversationMetadata = {
        conversationId: directLineAny.conversationId as string,
        channelId: "directline",
        serviceUrl: "https://europe.directline.botframework.com",
        user: null, // Will be populated from auth state
      };
      resolve(conversationMetadata);
      return;
    }

    // Subscribe to activity$ which triggers when conversation is established
    const subscription = directLine.activity$.subscribe({
      next: () => {
        if (directLineAny.conversationId && !conversationMetadata) {
          clearTimeout(timeout);
          conversationMetadata = {
            conversationId: directLineAny.conversationId as string,
            channelId: "directline",
            serviceUrl: "https://europe.directline.botframework.com",
            user: null, // Will be populated from auth state
          };

          subscription.unsubscribe();
          resolve(conversationMetadata);
        }
      },
      error: (err: Error) => {
        clearTimeout(timeout);
        subscription.unsubscribe();
        reject(err);
      },
    });

    // Also listen to connectionStatus$ as fallback
    const statusSubscription = directLine.connectionStatus$.subscribe({
      next: (status: ConnectionStatus) => {
        if (
          status === ConnectionStatus.Online &&
          directLineAny.conversationId &&
          !conversationMetadata
        ) {
          // Online
          clearTimeout(timeout);
          conversationMetadata = {
            conversationId: directLineAny.conversationId as string,
            channelId: "directline",
            serviceUrl: "https://europe.directline.botframework.com",
            user: null, // Will be populated from auth state
          };
          subscription.unsubscribe();
          statusSubscription.unsubscribe();
          resolve(conversationMetadata);
        }
      },
    });
  });
}

/**
 * Attach user identity to activity channelData (Phase 1: SharePoint Permissions).
 *
 * CRITICAL: This function MUST be called for every activity sent to the bot.
 * Without userPassport and delegatedToken, the backend will reject the turn
 * when REQUIRE_USER_IDENTITY=true.
 *
 * @param {Object} activity - Bot Framework activity
 * @param {string} delegatedGraphToken - Fresh Microsoft Graph delegated token
 * @returns {Object} Activity with identity data in channelData
 */
export function attachIdentityToActivity(
  activity: ActivityWithChannelData,
  delegatedGraphToken: string | null
): ActivityWithChannelData {
  if (!activity.channelData) {
    activity.channelData = {};
  }

  // Phase 1: Attach passport and delegated token
  if (userPassport) {
    activity.channelData.userPassport = userPassport;
  } else {
    console.warn("⚠️ No user passport available - backend identity validation will fail");
  }

  if (delegatedGraphToken) {
    activity.channelData.delegatedToken = delegatedGraphToken;
  } else {
    console.warn("⚠️ No delegated Graph token provided - backend identity validation will fail");
  }

  // Attach PR identifier if available
  const prIdentifier = import.meta.env.VITE_DEALOS_PR;
  if (prIdentifier) {
    activity.channelData.dealos_pr = prIdentifier;
  }

  return activity;
}

/**
 * Check whether the current DirectLine instance is healthy (connected and usable).
 * Synchronous, non-blocking — reads the BehaviorSubject's current value.
 *
 * @returns {boolean} true if DirectLine exists and connectionStatus is Online
 */
export function isDirectLineHealthy(): boolean {
  if (!directLine) {
    return false;
  }

  try {
    const status = directLine.connectionStatus$?.value;
    return status === ConnectionStatus.Online;
  } catch {
    return false;
  }
}

/**
 * Check if DirectLine is in a terminal (unrecoverable) state that requires full reconnection.
 * Terminal states: ExpiredToken, FailedToConnect, Ended, or no instance at all.
 * Transient states (Uninitialized, Connecting) are NOT terminal — they just need time.
 */
export function isDirectLineTerminal(): boolean {
  if (!directLine) return true;
  try {
    const status = directLine.connectionStatus$?.value;
    return status === ConnectionStatus.ExpiredToken
      || status === ConnectionStatus.FailedToConnect
      || status === ConnectionStatus.Ended;
  } catch {
    return true;
  }
}

export async function resetDirectLine(): Promise<void> {

  // Stop any active health probes first
  stopHealthProbe();

  // Phase 1: Clear passport and identity
  userPassport = null;
  userEntraOid = null;
  passportExpiresAt = null;

  if (directLine) {
    // Wait for the connection to fully close before proceeding
    await new Promise<void>((resolve) => {
      const statusSubscription = directLine!.connectionStatus$.subscribe({
        next: (status: ConnectionStatus) => {
          // ConnectionStatus: 0=Uninitialized, 1=Connecting, 2=Online, 3=ExpiredToken, 4=FailedToConnect, 5=Ended
          if (status === ConnectionStatus.Ended || status === ConnectionStatus.FailedToConnect) {
            // Ended or FailedToConnect

            statusSubscription.unsubscribe();
            resolve();
          }
        },
        error: () => {
          statusSubscription.unsubscribe();
          resolve(); // Still resolve to unblock
        },
      });

      // Call end() after setting up the listener
      directLine!.end();

      // Fallback timeout in case status never changes
      setTimeout(() => {
        statusSubscription.unsubscribe();
        resolve();
      }, 2000); // 2 second timeout
    });

    directLine = null;
  }
  conversationMetadata = null;
}
